/* eslint-disable no-async-promise-executor */
import moment from "moment";
import * as AWS from "aws-sdk";
import type {
	S3StsCredentials,
	S3UploadResult,
	StsToken,
} from "../model/Uploads";
import type { ResponseData } from "../model/Responses";
import config from "../config";
import apiCaller from "./axios.service";
import OSS from "ali-oss";
import { hybridGenerateSTSToken } from "./hybridPlayer.service";
import { getHybridPlayerIdFromUserToken } from "../utils/hybridSession";
import { v4 as uuidv4 } from "uuid";
import { generateChatSTSToken } from "./publicChat.service";

const baseUrl: string = "/user";

export const generateSTSToken = async <T>(): Promise<T> => {
	return apiCaller(`${baseUrl}/generateSTSToken`, {});
};

/**
 * `public/general/transaction/{HHmmssSSS}_hybrid_{hybridPlayerId}_{YYYYMMDDHHmmssSSS}{ext}`
 * Example: `general/transaction/160733022_hybrid_70_20260514160729835.jpg`
 */
export function buildHybridDepositReceiptObjectKey(
	file: File,
	hybridPlayerId: number
): string {
	const m = moment();
	const prefix = m.format("HHmmssSSS");
	const stamp = m.format("YYYYMMDDHHmmssSSS");
	const rawName = file.name || "receipt";
	const dot = rawName.lastIndexOf(".");
	const ext =
		dot >= 0
			? rawName
					.slice(dot)
					.toLowerCase()
					.replace(/[^.a-z0-9]/g, "") || ".bin"
			: ".bin";
	return `public/general/transaction/${prefix}_hybrid_${hybridPlayerId}_${stamp}${ext}`;
}

/** Upload one object to S3 with a full object key (path inside bucket). */
export const uploadS3Object = async ({
	file,
	token,
	objectKey,
}: {
	file: File;
	token: S3StsCredentials & Record<string, unknown>;
	objectKey: string;
}): Promise<S3UploadResult | false> => {
	const s3 = new AWS.S3({
		accessKeyId: token.accessKeyId,
		secretAccessKey: token.secretAccessKey,
		sessionToken: token.sessionToken,
		region: token.region,
		endpoint: token.endPoint,
		signatureVersion: "v4",
	});

	const contentType =
		file.type && file.type.length > 0 ? file.type : "application/octet-stream";

	const params: AWS.S3.PutObjectRequest = {
		Bucket: token.bucket,
		Key: objectKey,
		Body: file,
		ContentType: contentType,
		ACL: "public-read",
	};

	try {
		const managedUpload = s3.upload(params);
		const result = await managedUpload.promise();

		const responseData: S3UploadResult = {
			url: result.Location ?? "",
			name: result.Key ?? "",
		};

		const regionKey = config.region;
		if (result.Key?.startsWith(`public/${regionKey}/Upload/`)) {
			responseData.name = result.Key.replace(
				`public/${regionKey}/Upload/`,
				"Upload/"
			);
		} else if (result.Key?.startsWith("public/general/")) {
			responseData.name = result.Key.replace("public/general/", "general/");
		}

		return responseData.url ? responseData : false;
	} catch (error) {
		console.error("Upload to S3 failed:", error);
		return false;
	}
};

/** Hybrid deposit receipt on `/deposit/confirm`: STS + `public/general/transaction/...` key. */
export const uploadHybridDepositReceiptToS3 = async (
	file: File
): Promise<S3UploadResult | false> => {
	const hybridPlayerId = getHybridPlayerIdFromUserToken();
	if (hybridPlayerId == null) {
		return false;
	}

	const result =
		(await hybridGenerateSTSToken()) as ResponseData<S3StsCredentials>;
	if (!result?.status || !result.data) {
		return false;
	}
	const creds = result.data;
	if (
		!creds.accessKeyId ||
		!creds.secretAccessKey ||
		!creds.bucket ||
		!creds.region
	) {
		return false;
	}

	const objectKey = buildHybridDepositReceiptObjectKey(file, hybridPlayerId);
	return uploadS3Object({
		file,
		token: creds,
		objectKey,
	});
};

export const uploadOss = async ({
	file,
	token,
	fileType,
}: {
	file: any;
	token: any;
	fileType: string;
}): Promise<any> => {
	const oss = new OSS({
		region: token.region,
		accessKeyId: token.access_key_id,
		accessKeySecret: token.access_key_secret,
		bucket: token.bucket,
		endpoint: token.end_point,
		stsToken: token.security_token,
	});
	const fileName = file.name;
	try {
		const result: any = await oss.multipartUpload(
			`public/Upload/${fileType}_${moment().format("hhmmssSSS")}_${fileName}`,
			file,
			{
				//     parallel,
				//     partSize,
				// progress: onMultipartUploadProgress,
			}
		);
		result.url = await accessUrl(result, oss);
		result.fileName = fileName;
		if (result.url) {
			return result;
		} else {
			return false;
		}
	} catch (error) {
		return false;
	}
};

export async function accessUrl(file: any, ossConfig: any) {
	return new Promise(async (resolve, reject) => {
		try {
			let result = await ossConfig.signatureUrl(file.name, { expires: 3600 });
			result = result.replace(new RegExp(`http://`, `g`), `https://`);
			resolve(result);
		} catch (error) {
			reject(error);
		}
	});
}

export const uploadSingleFile = async ({
	file,
	fileType,
}: {
	file: any;
	fileType: string;
}): Promise<any> => {
	const result: ResponseData<StsToken> = await generateSTSToken();
	if (result.status) {
		const uploadOssResult: any = await uploadOss({
			file: file,
			token: result.data,
			fileType,
		});
		return uploadOssResult;
	} else {
		return false;
	}
};

/**
 * Builds S3 object key for chat attachments.
 * Pattern: `public/chat/{hybridPlayerId}/{yyyy-mm}/{yyyyMMddHHmmssSSS}_{uuid}{ext}`
 * Example: `public/chat/70/2026-05/20260514160729835_3f2a1b4c.jpg`
 */
export const buildChatAttachmentObjectKey = (
	file: File,
	identifier: string | number
): string => {
	const m = moment();
	const monthFolder = m.format("YYYY-MM");
	const timestamp = m.format("YYYYMMDDHHmmssSSS");
	const uid = uuidv4().replace(/-/g, "").slice(0, 8); // short uuid suffix

	const ext = extractSafeExtension(file.name);

	return `public/chat/${identifier}/${monthFolder}/${timestamp}_${uid}${ext}`;
};

/**
 * Extracts and sanitizes file extension.
 * Falls back to `.bin` if missing or invalid.
 */
const extractSafeExtension = (filename: string): string => {
	if (!filename) return ".bin";

	const extension = filename.split(".").pop()?.toLowerCase();

	if (!extension) return ".bin";

	const STORAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png"]);

	return STORAGE_EXTENSIONS.has(extension) ? `.${extension}` : ".bin";
};

/**
 * Chat attachment upload via STS credentials.
 */
export const uploadChatAttachmentToS3 = async (
	file: File,
	conversationId: number
): Promise<S3UploadResult | false> => {
	if (conversationId == null || conversationId == undefined) return false;

	const result =
		(await hybridGenerateSTSToken()) as ResponseData<S3StsCredentials>;
	if (!result?.status || !result.data) return false;

	const { accessKeyId, secretAccessKey, bucket, region } = result.data;
	if (!accessKeyId || !secretAccessKey || !bucket || !region) return false;

	const objectKey = buildChatAttachmentObjectKey(file, conversationId);

	return uploadS3Object({
		file,
		token: result.data,
		objectKey,
	});
};

export const uploadPublicChatAttachmentToS3 = async (
	file: File,
	identifier: string | number
): Promise<S3UploadResult | false> => {
	if (identifier == null || identifier == undefined) return false;

	const result =
		(await generateChatSTSToken()) as ResponseData<S3StsCredentials>;
	if (!result?.status || !result.data) return false;

	const { accessKeyId, secretAccessKey, bucket, region } = result.data;
	if (!accessKeyId || !secretAccessKey || !bucket || !region) return false;

	const objectKey = buildChatAttachmentObjectKey(file, identifier);

	return uploadS3Object({
		file,
		token: result.data,
		objectKey,
	});
};
